import { getPersistentDeviceId, PRODUCT_TYPES } from "@/lib/constants";
import { INTERNAL_API_ENDPOINTS, RTK_TAGS } from "@/lib/constants/Api";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const internalApi = createApi({
  reducerPath: "internal",
  tagTypes: [
    RTK_TAGS.APPLICATION_TAG,
    RTK_TAGS.INSURANCE_TAG,
    RTK_TAGS.DIGIKYC_TAG,
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}`,
    prepareHeaders: async (headers) => {
      const token = await sessionStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      const deviceId = getPersistentDeviceId();
      headers.set("x-device-type", `web`);
      headers.set("x-device-id", deviceId);
      headers.set("x-device-name", `${window?.navigator?.userAgent}`);
      return headers;
    },
  }),
  endpoints: (builder) => {
    return {
      productDetail: builder.query({
        query: ({ product }) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.PRODUCT_DETAILS.LoanDetails}?productType=${product}`,
            method: "GET",
          };
        },
      }),
      upiMandate: builder.mutation({
        query: (body) => {
          return {
            url: `${
              INTERNAL_API_ENDPOINTS.MANDATE_ENDPOINTS(body?.appId).UPI_MANDATE
            }`,
            method: "POST",
            body,
          };
        },
      }),
      upiMandateStatus: builder.mutation({
        query: (body) => {
          return {
            url: `${
              INTERNAL_API_ENDPOINTS.MANDATE_ENDPOINTS(body?.appId)
                .UPI_MANDATE_STATUS
            }`,
            method: "POST",
            body,
          };
        },
      }),
      getOffers: builder.query({
        query: ({ appId, isRPL }) => {
          return {
            url: isRPL
              ? INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(appId)?.GET_OFFER_RL
              : INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(appId)?.GET_OFFER,
            method: "GET",
          };
        },
      }),
      updateOffer: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(body?.appId)
              ?.SELECT_OFFER,
            method: "PATCH",
            body,
            invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
          };
        },
      }),
      updateOfferSlider: builder.mutation({
        query: ({ body, isRPL }) => {
          return {
            url: isRPL
              ? INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(body?.appId)
                  ?.SELECT_SLIDER_OFFER_RL
              : INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(body?.appId)
                  ?.SELECT_SLIDER_OFFER,
            method: "POST",
            body,
          };
        },
      }),
      selectedOfferTenure: builder.mutation({
        query: ({ body, isRPL }) => {
          return {
            url: isRPL
              ? INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(body?.appId)
                  ?.EDIT_SELECTED_OFFER_RL
              : INTERNAL_API_ENDPOINTS.OFFERS_ENDPOINTS(body?.appId)
                  ?.EDIT_SELECTED_OFFER,
            method: "POST",
            body,
          };
        },
      }),
      getInsurance: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.INSURANCE_ENDPOINT(body).GET_INSURANCE,
            method: "GET",
          };
        },
        providesTags: [RTK_TAGS.INSURANCE_TAG],
      }),
      updateInsurance: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.INSURANCE_ENDPOINT(body).INSURANCE,
            method: "PATCH",
            body,
          };
        },
        invalidatesTags: (result, error) =>
          error ? [] : [RTK_TAGS.INSURANCE_TAG],
      }),

      createLegality: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.LEEGALITY_ENDPOINTS(body?.appId)
              .LEEGALITY,
            method: "POST",
            body,
          };
        },
      }),
      getlegalityStatus: builder.query({
        query: (appId) => {
          return {
            url: INTERNAL_API_ENDPOINTS.LEEGALITY_ENDPOINTS(appId)
              .LEEGALITY_STATUS,
            method: "GET",
          };
        },
      }),

      initiateRpd: builder.mutation({
        query: (body) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS?.RPD(body?.appId)}`,
            method: "POST",
            body,
          };
        },
      }),
      vkycDetails: builder.query({
        query: (body) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS?.VKYC_DETAILS(body?.appId)}`,
            method: "GET",
          };
        },
      }),
      initiateVkyc: builder.mutation({
        query: (body) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS?.VKYC_CREATE(body?.appId)}`,
            method: "POST",
            body,
          };
        },
      }),

      kycStatus: builder.mutation({
        query: (body) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.KYC_ENDPOINT(body?.appId)}`,
            method: "POST",
            body,
          };
        },
      }),
      panLinkingStatusData: builder.query({
        query: (appId) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.CHECK_PAN_LINKING_STATUS(appId)}`,
            method: "GET",
          };
        },
        providesTags: [RTK_TAGS.DIGIKYC_TAG],
      }),
      checkPanLinkiningStatus: builder.mutation({
        query: (body) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.CHECK_PAN_LINKING_STATUS(
              body?.appId,
            )}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.DIGIKYC_TAG],
      }),
      bankMandate: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.MANDATE_ENDPOINTS(body?.appId)
              .BANK_MANDATE,
            method: "POST",
            body,
          };
        },
      }),
      bankMandateStatus: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.MANDATE_ENDPOINTS(body?.appId)
              .BANK_MANDATE_STATUS,
            method: "POST",
            body,
          };
        },
      }),
      getBankList: builder.query({
        query: () => {
          return {
            url: INTERNAL_API_ENDPOINTS.IFSC_ENDPOINTS().GET_BANKS_NAME_LIST,
            method: "GET",
          };
        },
      }),
      getIfscBankList: builder.query({
        query: () => {
          return {
            url: INTERNAL_API_ENDPOINTS.IFSC_ENDPOINTS().GET_BANKS_LIST,
            method: "GET",
          };
        },
      }),
      getIfscBanksStateList: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.IFSC_ENDPOINTS(body)
              .GET_IFSC_BANKS_STATE,
            method: "GET",
          };
        },
      }),
      getIfscBanksCitiesList: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.IFSC_ENDPOINTS(body)
              .GET_IFSC_BANKS_CITY,
            method: "GET",
          };
        },
      }),
      getIfscBanksAddressList: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.IFSC_ENDPOINTS(body)
              .GET_IFSC_BANKS_AREA,
            method: "GET",
          };
        },
      }),
      getIfscBanksIfsc: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.IFSC_ENDPOINTS(body).GET_IFSC_BANKS,
            method: "GET",
          };
        },
      }),
      createBankUrl: builder.mutation({
        query: (body) => {
          return {
            url: `/app/${body?.appId}/higheramount`,
            method: "POST",
            body,
          };
        },
      }),
      rpdmodes: builder.query({
        query: (appId) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.RPD_MODES(appId)}`,
            method: "GET",
          };
        },
      }),
      confirmRpdMode: builder.mutation({
        query: (body) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.RPD_MODES(body?.appId)}`,
            body,
            method: "POST",
          };
        },
      }),
      rpdStatus: builder.query({
        query: ({ appId }) => {
          return {
            url: `${INTERNAL_API_ENDPOINTS.RPD_STATUS(appId)}`,
            method: "GET",
          };
        },
      }),
      bankPennyDrop: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.PENNY_DROP(body?.appId),
            method: "POST",
            body,
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
      triggerUcic: builder.query({
        query: ({ appId, stage }) => {
          return {
            url: INTERNAL_API_ENDPOINTS.TRIGGER_UCIC(appId, stage),
            method: "GET",
          };
        },
      }),

      // ekyc
      initiateEkyc: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.EKYC_ENDPOINTS(body?.appId)
              ?.EKYC_INITIATE,
            method: "POST",
            body,
          };
        },
      }),
      sendBeaconEkycPerformed: builder.mutation({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.EKYC_ENDPOINTS(body?.appId).EKYC_COMPLETE,
            method: "POST",
            body,
          };
        },
      }),
      ekycStatus: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.EKYC_ENDPOINTS(body?.appId)
              ?.EKYC_STATUS,
            method: "GET",
          };
        },
        // invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
      juspayOrderStatus: builder.query({
        query: (body) => {
          return {
            url: INTERNAL_API_ENDPOINTS.MANDATE_ENDPOINTS(body?.orderId)
              .JUSPAY_ORDER_STATUS,
            method: "GET",
          };
        },
        invalidatesTags: [RTK_TAGS.APPLICATION_TAG],
      }),
    };
  },
});

export const {
  useUpiMandateMutation,
  useUpiMandateStatusMutation,
  useProductDetailQuery,
  useGetOffersQuery,
  useUpdateOfferMutation,
  useUpdateOfferSliderMutation,
  useCreateLegalityMutation,
  useInitiateRpdMutation,
  useVkycDetailsQuery,
  useInitiateVkycMutation,
  useKycStatusMutation,
  usePanLinkingStatusDataQuery,
  useCheckPanLinkiningStatusMutation,
  useBankMandateMutation,
  useBankMandateStatusMutation,
  useGetBankListQuery,
  useGetIfscBankListQuery,
  useGetIfscBanksStateListQuery,
  useGetIfscBanksCitiesListQuery,
  useGetIfscBanksAddressListQuery,
  useGetIfscBanksIfscQuery,
  useCreateBankUrlMutation,
  useRpdmodesQuery,
  useConfirmRpdModeMutation,
  useGetInsuranceQuery,
  useUpdateInsuranceMutation,
  useSelectedOfferTenureMutation,
  useRpdStatusQuery,
  useLazyGetlegalityStatusQuery,
  useBankPennyDropMutation,
  useLazyTriggerUcicQuery,
  useInitiateEkycMutation,
  // useEkycConsentMutation,
  useLazyEkycStatusQuery,
  useSendBeaconEkycPerformedMutation,
  useLazyJuspayOrderStatusQuery,
} = internalApi;
export { internalApi };
